import { useCallback, useEffect, useState } from 'react'

import { Button, Col, Form, Input, Row, Switch } from 'antd'
import { getTimezoneOffset } from 'date-fns-tz'
import { FormattedMessage, useIntl } from 'react-intl'

import { MultiDayTooltip } from 'components/AvalancheForecast/Setup/MultiDayTooltip'
import { LimitedColourPicker } from 'components/ColorPicker/LimitedColourPicker'
import { Select } from 'components/Dropdown/Select'
import { Instructions } from 'components/Map/Instructions'
import { RangePicker } from 'components/RangePicker/RangePicker'
import { useActiveAvalancheForecast, useAvalancheForecasts } from 'hooks/avalancheForecasts'
import { useSettings } from 'hooks/Settings'
import { postAvalancheForecast } from 'services/AvalancheForecast'
import messages from 'services/intl/messageDefinitions'
import { AvalancheForecastStore } from 'stores/AvalancheForecastStore'
import { useId, useLocale } from 'stores/UserStore'
import { createEmptyAvalancheForecast, DAY_ONE } from 'utils/AvalancheForecast'
import { useSendNotification } from 'utils/Notifications'
import { COLOUR_HEXES, COLOUR_MAP, COLOUR_NAMES, TIMEZONE_OPTIONS } from 'utils/Product'
import { useForecasters } from 'hooks/forecasters'

export const CreateForm = ({
    toggleEdit,
    update,
    initialValues = createEmptyAvalancheForecast(),
    editing,
    closeDrawer,
    autoUpdate = false,
}) => {
    const intl = useIntl()
    const [form] = Form.useForm()

    const userId = useId()
    const draftForecast = AvalancheForecastStore.useDraftProduct()
    const updateDraftForecast = AvalancheForecastStore.useUpdateDraftProduct()
    const resetDraft = AvalancheForecastStore.useResetDraftProduct()
    const locale = useLocale()
    const setActiveId = AvalancheForecastStore.useSetActiveProductId()
    const activeProduct = useActiveAvalancheForecast()
    const sendNotification = useSendNotification()
    const { mutate } = useAvalancheForecasts()
    const { forecasters } = useForecasters()

    const [disabledForm, setDisabledForm] = useState(editing && !initialValues.userID)
    const [dateRangeError, setDateRangeError] = useState(false)
    const [name, setName] = useState(initialValues.name)
    const [nameTouched, setNameTouched] = useState(false)
    const [dateRange, setDateRange] = useState([
        new Date(initialValues.issueDateTime),
        new Date(initialValues.expiryDateTime),
    ])
    const [timezone, setTimezone] = useState(initialValues.timezone)
    const [isMultiDay, setIsMultiDay] = useState(initialValues.isMultiDay)
    const [colour, setColour] = useState(editing ? initialValues.colour : draftForecast.colour)
    const { data: settings } = useSettings()
    const { multiDayForecasts, showTimezoneSelector } = settings?.data?.featureFlags || {}

    // Set the user ID to the current user if it is not set and we're not editing an existing forecast (ie a cloned forecast)
    const formattedUserId = initialValues.userID ? initialValues.userID : editing ? null : userId

    const createNewAvalancheForecast = formData => {
        const newSetup = createEmptyAvalancheForecast(locale)
        newSetup.colour = draftForecast.colour // TODO have only a single source of truth
        newSetup.status = 'draft'
        newSetup.name = formData.name
        newSetup.userID = formData.userID
        newSetup.issueDateTime = dateRange[0]
        newSetup.expiryDateTime = dateRange[1]
        newSetup.polygons = draftForecast.polygons // TODO have only a single source of truth
        newSetup.dayOne = formData.dayOne
        newSetup.timezone = formData.timezone || process.env.REACT_APP_DEFAULT_TIMEZONE
        newSetup.isMultiDay = formData.isMultiDay || false

        apiPost(newSetup, locale)
    }

    const apiPost = useCallback(async (data, locale) => {
        await postAvalancheForecast(data, locale)
        mutate()
        resetDraft()
        sendNotification(intl.formatMessage({ ...messages.avalancheForecastSaved }, { name: data.name }))
        closeDrawer()
        form.resetFields()
    }, [])

    const cancelDraft = () => {
        closeDrawer()
        form.resetFields()
        setColour(COLOUR_HEXES[0])
    }

    useEffect(() => {
        if (!activeProduct) {
            setActiveId('draft')
        }
    }, [activeProduct, setActiveId])

    const autoChecker = changeValues => {
        const changedField = Object.keys(changeValues)[0]
        const parsedValue = Object.values(changeValues)[0]
        const updateData = [{ key: changedField, value: parsedValue }]

        if (changedField === 'name') {
            // remove trailing or precedding white space
            const formattedName = parsedValue.trim()
            // Trigger the debounce timer
            if (formattedName.length >= 3 && formattedName.length <= 100) {
                setName(formattedName)
                setNameTouched(true)
            }
            // Return early. We have a debounce that will run the update function
            return
        }

        // The date range is handled in the handleDateChange function
        if (changedField === 'times') return

        // Now that there is a forecaster allow for editing other fields
        if (changedField === 'userID') {
            setDisabledForm(false)
        }

        // Use local var for faster responsiveness
        if (changedField === 'isMultiDay') {
            setIsMultiDay(parsedValue)
        }

        if (changedField === 'timezone') {
            const oldTimezone = timezone
            const newTimezone = parsedValue
            const oldTimezoneOffset = getTimezoneOffset(oldTimezone)
            const newTimezoneOffset = getTimezoneOffset(newTimezone)
            const timezoneDifference = oldTimezoneOffset - newTimezoneOffset

            const newIssueDateTime = new Date(new Date(dateRange[0]).getTime() + timezoneDifference)
            const newExpiryDateTime = new Date(new Date(dateRange[1]).getTime() + timezoneDifference)
            updateData.push({ key: 'issueDateTime', value: newIssueDateTime })
            updateData.push({ key: 'expiryDateTime', value: newExpiryDateTime })

            setTimezone(newTimezone)
            setDateRange([newIssueDateTime, newExpiryDateTime])
        }

        if (autoUpdate && updateData[0].value !== undefined && updateData[0].key) {
            update(updateData)
        }
    }

    const handleSubmit = () => {
        if (dateRange[0] > dateRange[1]) {
            setDateRangeError(true)
            return
        }

        if (editing) {
            toggleEdit()
        } else {
            form.submit()
        }
    }

    const handleDateChange = dateRange => {
        if (dateRange[0] > dateRange[1]) {
            setDateRangeError(true)
            return
        }

        setDateRange(dateRange)
        setDateRangeError(false)

        if (dateRange[0] && dateRange[1] && update) {
            const newDateData = [
                { key: 'expiryDateTime', value: dateRange[1] },
                { key: 'issueDateTime', value: dateRange[0] },
            ]
            update(newDateData)
        }
    }

    const handleColourChange = useCallback(
        colour => {
            setColour(colour)
            if (editing && autoUpdate) {
                const updateData = {
                    key: 'colour',
                    value: colour,
                }
                update([updateData])
            } else {
                updateDraftForecast({
                    ...draftForecast,
                    colour,
                })
            }
        },
        [autoUpdate, draftForecast, editing, update, updateDraftForecast]
    )

    // prevent multiple PUT request when user is typing
    useEffect(() => {
        const handler = setTimeout(() => {
            if (autoUpdate && nameTouched) {
                const updateData = {
                    key: 'name',
                    value: name,
                }
                update([updateData])
                setNameTouched(false)
            }
        }, 1500)

        return () => {
            clearTimeout(handler)
        }
    }, [autoUpdate, name, nameTouched, update])

    return (
        <div>
            {editing ? (
                <h1 style={styles.title}>
                    <FormattedMessage {...messages.editAvalancheForecast} />
                </h1>
            ) : (
                <h1 style={styles.title}>
                    <FormattedMessage {...messages.createAvalancheForecast} />
                </h1>
            )}
            <Instructions />
            <Form
                form={form}
                layout="horizontal"
                onFinish={createNewAvalancheForecast}
                autoComplete="on"
                onValuesChange={autoChecker}
                initialValues={{ ...initialValues, userID: formattedUserId, times: dateRange }}
            >
                <Form.Item
                    label={<FormattedMessage {...messages.forecastName} />}
                    labelCol={{ span: 24 }}
                    name="name"
                    rules={[
                        { required: true, message: <FormattedMessage {...messages.nameRequired} /> },
                        { min: 3, message: <FormattedMessage {...messages.nameTooShort} /> },
                        { max: 100, message: <FormattedMessage {...messages.nameTooLong} /> },
                    ]}
                >
                    <Input disabled={disabledForm} />
                </Form.Item>
                <Row gutter={16}>
                    <Col className="gutter-row" span={12}>
                        <Form.Item
                            label={<FormattedMessage {...messages.forecaster} />}
                            labelCol={{ span: 24 }}
                            name="userID"
                            rules={[{ required: true }]}
                            data-test={'forecasterSelect'}
                        >
                            <Select>
                                {forecasters?.map((item, i) => (
                                    <Select.Option key={i} value={item.id}>
                                        {item.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col className="gutter-row" span={12}>
                        <Form.Item
                            label={<FormattedMessage {...messages.colour} />}
                            labelCol={{ span: 24 }}
                            name="colour"
                            rules={[{ required: false }]}
                        >
                            <LimitedColourPicker
                                colourMap={COLOUR_MAP}
                                colourNames={COLOUR_NAMES}
                                disabled={disabledForm}
                                initialColour={colour}
                                onUpdate={handleColourChange}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item
                    label={<FormattedMessage {...messages.issueAndExpiry} />}
                    labelCol={{ span: 24 }}
                    name="times"
                    value={dateRange}
                    rules={[{ required: true, message: <FormattedMessage {...messages.forecastValidationDate} /> }]}
                >
                    <RangePicker
                        initValues={dateRange}
                        onChange={handleDateChange}
                        disabled={disabledForm}
                        dateRangeError={dateRangeError}
                        timezone={timezone}
                    />
                </Form.Item>
                {multiDayForecasts && showTimezoneSelector && (
                    <Row gutter={16}>
                        <Col className="gutter-row" span={12}>
                            <Form.Item
                                rules={[{ required: true }]}
                                label={<FormattedMessage {...messages.timezone} />}
                                labelCol={{ span: 24 }}
                                name="timezone"
                            >
                                <Select disabled={disabledForm}>
                                    {Object.values(TIMEZONE_OPTIONS).map(option => (
                                        <Select.Option key={option.value} value={option.value}>
                                            {intl.formatMessage({ ...messages[option.translationId] })}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        {multiDayForecasts && (
                            <Col className="gutter-row" span={12}>
                                <Form.Item
                                    rules={[{ required: true }]}
                                    label={
                                        <>
                                            <FormattedMessage {...messages.multiDayForecast} />
                                            <MultiDayTooltip />
                                        </>
                                    }
                                    labelCol={{ span: 24 }}
                                    name="isMultiDay"
                                >
                                    <Switch
                                        disabled={disabledForm}
                                        checked={isMultiDay}
                                        onChange={checked => {
                                            setIsMultiDay(checked)
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        )}
                    </Row>
                )}
                <Row gutter={16}>
                    <Col className="gutter-row" span={12}>
                        <Form.Item
                            rules={[{ required: true }]}
                            label={<FormattedMessage {...messages.dayOne} />}
                            labelCol={{ span: 24 }}
                            name="dayOne"
                        >
                            <Select disabled={disabledForm}>
                                {[...DAY_ONE].map(key => (
                                    <Select.Option key={key} value={key}>
                                        <FormattedMessage {...messages[key]} />
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            <DoneButtons editing={editing} handleSubmit={handleSubmit} cancelDraft={cancelDraft} />
        </div>
    )
}

const DoneButtons = ({ editing, handleSubmit, cancelDraft }) => {
    if (editing) {
        return (
            <div style={styles.buttonContainer}>
                <Button type="primary" onClick={handleSubmit} data-test={'closeEditingButton'}>
                    <FormattedMessage {...messages.closeEditing} />
                </Button>
            </div>
        )
    }

    return (
        <div style={styles.buttonContainer}>
            <Button onClick={cancelDraft} style={styles.cancelButton}>
                <FormattedMessage {...messages.cancel} />
            </Button>
            <Button type="primary" onClick={handleSubmit} data-test={'createForecastSubmitButton'}>
                <FormattedMessage {...messages.create} />
            </Button>
        </div>
    )
}

const styles = {
    button: {
        margin: '0 var(--s0) 0 0',
        color: '#fff',
    },
    closeEditing: {
        float: 'right',
        marginBottom: 'var(--s1)',
    },
    container: {
        display: 'flex',
    },
    cancelButton: {
        margin: '0 var(--s0) 0 0',
        color: '#fff',
        background: 'var(--grey)',
        borderColor: 'var(--grey)',
    },
    deleteButton: {
        marginTop: 'var(--s1)',
        marginLeft: 'auto',
        position: 'fixed',
        bottom: 'var(--s0)',
        right: 'var(--s0)',
    },
    buttonContainer: {
        display: 'flex',
        alignItems: 'right',
        justifyContent: 'right',
    },
    errorMessage: {
        fontSize: '14px',
        marginTop: '-25px',
        color: 'var(--red)',
        fontWeight: '300',
    },
    hide: {
        display: 'none',
    },
    show: {
        display: 'block',
    },
    aspectRose: {
        marginBottom: 'var(--s0)',
    },
    sudoLabel: {
        fontSize: '14px',
    },
    title: {
        fontWeight: 300,
    },
}
